import React, { ComponentType, HTMLAttributes, useMemo, useState } from 'react';
import { styled } from '@mui/material';

import ModalDialog from '@/components/ModalDialog';
import hinted from '@/components/Hinted';

import LinkIcon from '@/components/icons/Link';
import t from '@/utils/translate';

type Props = {
  shareCode: string | null
  open: boolean,
  onClose: () => void,
  share: () => void,
  conceal: () => void,
};

type InfoProps = {
  color?: string,
  emphasis?: boolean
} & HTMLAttributes<HTMLParagraphElement>;

const Info: ComponentType<InfoProps> = styled('p')(({ color = '#091E42', emphasis = false }: InfoProps) => ({
  fontSize: emphasis ? '14px' : '13px',
  fontWeight: emphasis ? 500 : 400,
  textAlign: 'left',
  margin: '0px',
  color,
}));

const Button = styled('button')(({ color = '#0052CC' }) => ({
  marginBottom: '10px',
  borderRadius: '5px',
  padding: '8px 15px',
  background: color,
  color: '#FFFFFF',
  fontWeight: 400,
  fontSize: '13px',
  border: 'none',
  cursor: 'pointer',
}));


const ShareLink = hinted(
  styled('p')({
    margin: '10px 0 0 0',
    cursor: 'pointer',
    userSelect: 'none',
    border: '1px solid #C1C7D0',
    borderRadius: '4px',
    color: '#6B778C',
    fontSize: '14px',
    fontWeight: 400,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '32px',
    paddingLeft: '10px',
    paddingRight: '6px',
  }),
  {
    placement: 'bottom-start',
    delay: 250,
    fadeDuration: 50,
    hint: t('action.copy')
  }
);

const CopyableShareLink = ({ code }: { code: string }) => {
  const [ isCopied, setIsCopied ] = useState(false);

  const link = useMemo<URL>(() => new URL(`sp/${code}`, window.location.origin), [ code ]);

  return <ShareLink
    hint={isCopied ? t('state.copied') : t('action.copy') }
    onClick={(e) => {
      navigator.clipboard.writeText(link.href);
      setIsCopied(true);
    }}
    onMouseLeave={(e) => {
      setIsCopied(false);
    }}
  >
    <span>{link.href}</span>
    <LinkIcon size={20} fill='#C1C7D0'/>
  </ShareLink>
};

const StyledDialog = styled(ModalDialog)({
  ['& div.MuiDialogContent-root']: {
    marginTop: '20px',
  }
});

export default function ({ shareCode, open, onClose, share, conceal }: Props) {
  return (
    <StyledDialog
      title={t('project.title.share')}
      open={open}
      onClose={onClose}
      disableEscapeKeyDown
      width='525px'
    >
      {shareCode
        ? <>
          <Info color='#00875A' emphasis={true}>{t('project.message.share.open', true)}</Info>
          <div>
            <Info>{t('project.hint.share.open.info1', true)}</Info>
            <Info color='#6B778C'>{t('project.hint.share.open.info2', true)}</Info>
            <CopyableShareLink code={shareCode} />
          </div>
          <div>
            <Button color='#DE350B' onClick={conceal}>{t('project.share.action.close')}</Button>
            <Info color='#DE350B'>{t('project.hint.share.close.info1', true)}</Info>
            <Info color='#6B778C'>{t('project.hint.share.close.info2', true)}</Info>
          </div>
        </>
        : <>
          <Info color='#6B778C' emphasis={true}>{t('project.message.share.closed', true)}</Info>
          <div>
            <Button onClick={share}>{t('project.share.action.open')}</Button>
            <Info>{t('project.hint.share.open.info1', true)}</Info>
            <Info color="#6B778C">{t('project.hint.share.open.info2', true)}</Info>
          </div>
        </>
      }
    </StyledDialog>
  );
}